import { useEffect, useState } from "react";
import { getToken, deleteToken } from "firebase/messaging";
import { useMutation } from "@tanstack/react-query";

import { Secret } from "../models/secret";
import { getMessagingInstance, getVapidKey } from "../services/firebase.service";
import NotificationService from "../services/notification.service";

export type PermissionStatus = "default" | "granted" | "denied" | "unsupported";

interface UseFCMTokenReturn {
  token: string | null;
  permission: PermissionStatus;
  isLoading: boolean;
  error: Error | null;
  requestPermission: () => Promise<void>;
}

export const useFCMToken = (
  secret: Secret | undefined,
  userId: number,
  enabled: boolean = true
): UseFCMTokenReturn => {
  const [token, setToken] = useState<string | null>(null);
  const [permission, setPermission] = useState<PermissionStatus>("default");
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { mutate: saveToken } = useMutation({
    mutationFn: (fcmToken: string) =>
      NotificationService.updatePushNotificationToken({
        token: fcmToken,
        userId,
        app: "shipeezi-web",
      }),
    onSuccess: () => {
      console.log("FCM token successfully saved to backend");
    },
    onError: (err: Error) => {
      console.error("Failed to save FCM token to backend:", err);
    },
  });

  const checkNotificationSupport = (): boolean => {
    if (!("Notification" in window)) {
      setPermission("unsupported");
      return false;
    }
    return true;
  };

  const requestPermission = async (): Promise<void> => {
    if (!checkNotificationSupport()) {
      console.warn("Notifications are not supported in this browser");
      return;
    }

    if (!secret) {
      console.warn("Secret not available, cannot request notification permission");
      return;
    }

    try {
      setIsLoading(true);

      const currentPermission = await Notification.requestPermission();
      setPermission(currentPermission as PermissionStatus);

      if (currentPermission === "granted") {
        await generateToken();
      } else if (currentPermission === "denied") {
        console.warn("Notification permission denied by user");
        setError(new Error("Notification permission denied"));
      }
    } catch (err) {
      const error = err as Error;
      console.error("Error requesting notification permission:", error);
      setError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const generateToken = async (): Promise<void> => {
    try {
      const messaging = getMessagingInstance();
      if (!messaging) {
        console.warn("Firebase Messaging not initialized");
        return;
      }

      const vapidKey = getVapidKey(secret!);
      if (!vapidKey) {
        console.warn("VAPID key not available in secret manager");
        return;
      }

      try {
        await deleteToken(messaging);
      } catch {
        console.log("No token was found!");
      }
      const currentToken = await getToken(messaging, { vapidKey });

      if (currentToken) {
        setToken(currentToken);
        saveToken(currentToken);
        console.log("FCM Token generated:", !!currentToken);
      } else {
        console.warn("No registration token available. Request permission to generate one.");
      }
    } catch (err) {
      const error = err as Error;
      console.error("An error occurred while retrieving token:", error);
      setError(error);
    }
  };

  const setupTokenRefreshListener = (): (() => void) | undefined => {
    const messaging = getMessagingInstance();
    if (!messaging) return undefined;

    const intervalId = setInterval(async () => {
      try {
        const vapidKey = getVapidKey(secret!);
        if (!vapidKey) return;

        try {
          await deleteToken(messaging);
        } catch {
          console.log("No token was found!");
        }
        const currentToken = await getToken(messaging, { vapidKey });
        if (currentToken && currentToken !== token) {
          console.log("Token changed, updating...");
          setToken(currentToken);
          saveToken(currentToken);
        }
      } catch (err) {
        console.error("Error checking token:", err);
      }
    }, 60 * 60 * 1000);

    return () => clearInterval(intervalId);
  };

  useEffect(() => {
    if (!enabled || !secret) return;

    if (
      window.location.pathname.includes("login") ||
      window.location.pathname.includes("update-password")
    )
      return;

    const init = async () => {
      if (!checkNotificationSupport()) return;

      const currentPermission = Notification.permission;
      setPermission(currentPermission as PermissionStatus);

      if (currentPermission === "granted") {
        await generateToken();
      }
    };

    init();

    const unsubscribe = setupTokenRefreshListener();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [enabled, secret, userId]);

  return {
    token,
    permission,
    isLoading,
    error,
    requestPermission,
  };
};

export default useFCMToken;
